import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { useAuthStore, useAgentStore, useModalStore, useUIStore, useToastStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import useRequireAuth, { useHasUser } from '@/hooks/useRequireAuth';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import useBotConversationPreviews from '@/hooks/useBotConversationPreviews';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SidebarListItemLayout from './SidebarListItemLayout';
import SidebarListItemAgent from './SidebarListItemAgent';

const PERSONAL_LIMIT = 5;
// Pagination objects are replaced on fetch/reset, so empty results can be cached
// without persisting account data or treating a cleared store as already loaded.
const loadedPages = new WeakSet();
const label = (t, key, fallback) => {
  const value = t(key);
  return typeof value === 'string' && value !== key ? value : fallback;
};

function SidebarSavedAgentsSection({ onNavigate } = {}) {
  const route = useCurrentRoute();
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const setSidebarVisible = useUIStore(state => state.setSidebarVisible);
  const { hasUser, isAuthResolved } = useHasUser();
  const userId = useAuthStore(state => state.user?.id);
  const botOrder = useUIStore(state => state.sidebarBotOrderByUser?.[userId]);
  const setBotOrder = useUIStore(state => state.setSidebarBotOrder);
  const ensureAllMyAgents = useAgentStore(state => state.ensureAllMyAgents);
  const addToast = useToastStore(state => state.actions.addToast);
  const headerRef = useRef(null);
  // Match Your Chats: track the entire DOM header, including nested controls.
  // RN Web hover responders can lose hover when crossing child elements.
  useEffect(() => {
    if (Platform.OS !== 'web' || isXS) return;
    const element = headerRef.current;
    if (!element) return;
    const enter = () => setHeaderHovered(true);
    const leave = () => setHeaderHovered(false);
    element.addEventListener('mouseenter', enter);
    element.addEventListener('mouseleave', leave);
    return () => {
      element.removeEventListener('mouseenter', enter);
      element.removeEventListener('mouseleave', leave);
    };
  }, [isXS]);
  const [headerHovered, setHeaderHovered] = useState(false);
  const [headerFocused, setHeaderFocused] = useState(false);
  const desktopControls = Platform.OS === 'macos' || (Platform.OS === 'web' && !isXS);
  const showHeaderControls = !desktopControls || headerHovered || headerFocused;
  const [isReordering, setIsReordering] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const draggedBot = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const clearDrag = () => { draggedBot.current = null; setDropTarget(null); };
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const favoriteAgentIds = useAuthStore(state => state.user?.favoriteAgentIds);
  const agents = useAgentStore(state => state.agents);
  const myAgentIds = useAgentStore(state => state.myAgentIds);
  const pagination = useAgentStore(state => state.pagination.myAgents);
  const isLoading = useAgentStore(state => state.isLoadingMyAgents);
  const error = useAgentStore(state => state.myAgentsError);
  const fetchMyAgents = useAgentStore(state => state.fetchMyAgents);
  const { openModal } = useModalStore(state => state.actions);
  const isCollapsed = useUIStore(state => Boolean(state.collapsedSections.savedAgents));
  const toggleSection = useUIStore(state => state.toggleSection);
  const toggleBots = useCallback(() => toggleSection('savedAgents'), [toggleSection]);
  const requireAuth = useRequireAuth();
  const startAgentWorkChat = useStartAgentWorkChat();
  const canShowPersonal = hasUser && Boolean(userId);
  const wasLoading = useRef(isLoading);

  useEffect(() => {
    if (wasLoading.current && !isLoading && !error && canShowPersonal) loadedPages.add(pagination);
    wasLoading.current = isLoading;
  }, [isLoading, error, pagination, canShowPersonal]);

  useEffect(() => {
    if (!canShowPersonal) return undefined;
    // Let the active screen's eager fetch run first, then inspect the live store
    // rather than the render snapshot (desktop and drawer can mount together).
    const timer = setTimeout(() => {
      const state = useAgentStore.getState();
      if (state.isLoadingMyAgents || state.myAgentsError || state.myAgentIds.length || loadedPages.has(state.pagination.myAgents)) return;
      void fetchMyAgents(1).then(() => {
        const latest = useAgentStore.getState();
        if (!latest.myAgentsError && useAuthStore.getState().user?.id === userId) {
          loadedPages.add(latest.pagination.myAgents);
        }
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [canShowPersonal, userId, fetchMyAgents]);

  const { personalAgents, savedAgents } = useMemo(() => {
    if (!canShowPersonal) return { personalAgents: [], savedAgents: [] };
    const ownedIds = new Set(myAgentIds);
    // A saved owned bot may not be in the first page of the personal listing.
    for (const id of favoriteAgentIds || []) {
      if (agents[id]?.isOwner) ownedIds.add(id);
    }
    const personalAgents = [];
    const savedAgents = [];
    for (const id of ownedIds) {
      if (agents[id]) personalAgents.push(agents[id]);
    }
    for (const id of new Set(favoriteAgentIds || [])) {
      if (!ownedIds.has(id) && agents[id]) savedAgents.push(agents[id]);
    }
    const rank = new Map((botOrder || []).map((id, index) => [id, index]));
    const byOrder = (a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity);
    return { personalAgents: personalAgents.sort(byOrder), savedAgents: savedAgents.sort(byOrder) };
  }, [canShowPersonal, myAgentIds, favoriteAgentIds, agents, botOrder]);

  const moveBot = (group, index, offset) => {
    const reordered = [...group];
    const target = index + offset;
    if (target < 0 || target >= reordered.length) return;
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const other = group === personalAgents ? savedAgents : personalAgents;
    setBotOrder(userId, [...reordered, ...other].map(bot => bot.id));
  };

  const toggleReordering = async () => {
    if (isReordering) { clearDrag(); setIsReordering(false); return; }
    setIsLoadingOrder(true);
    try {
      await ensureAllMyAgents();
      setIsReordering(true);
    } catch (error) {
      addToast(error.message || 'Could not load bots to reorder', 'error');
    } finally { setIsLoadingOrder(false); }
  };

  const visiblePersonalAgents = isReordering ? personalAgents : personalAgents.slice(0, PERSONAL_LIMIT);
  const visibleBotIds = useMemo(() => [
    ...visiblePersonalAgents, ...savedAgents,
  ].map(agent => agent.id), [personalAgents, savedAgents, isReordering]);
  const { previewsByBotId: conversationPreviews } = useBotConversationPreviews(visibleBotIds);
  const previewsByBotId = useMemo(() => Object.fromEntries(
    Object.entries(conversationPreviews).filter(([botId]) => isPlus || !agents[botId]?.flags?.isPlusOnly),
  ), [conversationPreviews, isPlus, agents]);

  const navigate = useCallback((href) => {
    router.push(href);
    onNavigate?.();
  }, [router, onNavigate]);

  const createBot = useCallback(() => requireAuth(() => openModal('CustomAgentModal'), {
    source: 'sidebar_create_bot',
    title: 'Sign in to create a bot',
    subtitle: 'Give your bot instructions for the work you do often.',
    icon: 'robot',
  }), [requireAuth, openModal]);

  const launchBot = useCallback(async (agent, source) => {
    const launched = await startAgentWorkChat(agent, { source, chooseMode: true });
    if (launched) {
      if (isXS) setSidebarVisible(false);
      onNavigate?.();
    }
  }, [startAgentWorkChat, onNavigate, isXS, setSidebarVisible]);

  const renderAgent = (agent, source) => (
    <SidebarListItemAgent
      key={agent.id}
      id={agent.id}
      title={agent.name}
      onPress={() => launchBot(agent, source)}
      isActive={route.isAgent(agent.id) || route.pathname === `/c/${previewsByBotId[agent.id]?.conversationId}`}
      imageUrl={agent.avatarPath || ''}
      shouldShowNewBadge={false}
    />
  );
  const renderOrderedAgent = (agent, index, group, source) => {
    if (Platform.OS === 'web' && !isXS && isReordering) {
      const position = event => event.clientY < event.currentTarget.getBoundingClientRect().top + event.currentTarget.getBoundingClientRect().height / 2 ? 'before' : 'after';
      const canDrop = () => draggedBot.current && draggedBot.current !== agent.id && group.some(bot => bot.id === draggedBot.current);
      return (
        <div
          key={agent.id}
          data-testid={`reorder-bot-${agent.id}`}
          draggable
          tabIndex={0}
          role="button"
          aria-label={`Bot: ${agent.name}`}
          aria-description="Drag to reorder, or use the up and down arrow keys."
          onKeyDown={event => {
            if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
            event.preventDefault();
            event.stopPropagation();
            moveBot(group, index, event.key === 'ArrowUp' ? -1 : 1);
          }}
          onDragStart={event => {
            draggedBot.current = agent.id;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', agent.id);
          }}
          onDragOver={event => {
            if (!canDrop()) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            const edge = position(event);
            setDropTarget(current => current?.id === agent.id && current.edge === edge ? current : { id: agent.id, edge });
          }}
          onDragLeave={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) setDropTarget(null);
          }}
          onDrop={event => {
            event.preventDefault();
            if (canDrop()) {
              const reordered = group.filter(bot => bot.id !== draggedBot.current);
              const target = reordered.findIndex(bot => bot.id === agent.id) + (position(event) === 'after' ? 1 : 0);
              reordered.splice(target, 0, group.find(bot => bot.id === draggedBot.current));
              const other = group === personalAgents ? savedAgents : personalAgents;
              setBotOrder(userId, [...reordered, ...other].map(bot => bot.id));
            }
            clearDrag();
          }}
          onDragEnd={clearDrag}
          style={{ cursor: 'grab', borderTop: `2px solid ${dropTarget?.id === agent.id && dropTarget.edge === 'before' ? colors.text_color_primary : 'transparent'}`, borderBottom: `2px solid ${dropTarget?.id === agent.id && dropTarget.edge === 'after' ? colors.text_color_primary : 'transparent'}`, borderRadius: 8 }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 8, minHeight: 52 }}>
            <View style={styles.reorderGrip}>
              {[0, 1].map(column => <View key={column} style={styles.reorderGripColumn}>{[0, 1, 2].map(row => <View key={row} style={[styles.reorderGripDot, { backgroundColor: colors.text_color_secondary }]} />)}</View>)}
            </View>
            {!!agent.avatarPath && <Image source={{ uri: agent.avatarPath }} style={{ width: 32, height: 32, borderRadius: 16 }} contentFit="cover" />}
            <Text size="n" numberOfLines={1} style={{ flex: 1 }}>{agent.name}</Text>
          </View>
        </div>
      );
    }
    return (
    <View key={agent.id}>
      {renderAgent(agent, source)}
      {isReordering && (
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, paddingRight: 8 }}>
          {[-1, 1].map(offset => (
            <Pressable
              key={offset}
              accessibilityRole="button"
              accessibilityLabel={`Move ${agent.name} ${offset < 0 ? 'up' : 'down'}`}
              disabled={index + offset < 0 || index + offset >= group.length}
              onPress={() => moveBot(group, index, offset)}
              style={{ padding: 8, opacity: index + offset < 0 || index + offset >= group.length ? 0.3 : 1 }}
            >
              <Icon name={offset < 0 ? 'arrow-up' : 'arrow-down'} size={14} color={colors.text_color_primary} />
            </Pressable>
          ))}
        </View>
      )}
    </View>
    );
  };
  const HeaderContainer = Platform.OS === 'web' ? View : Pressable;
  const createLabel = label(t, 'pages.agents.intro.createCta', 'Create a bot');
  const hasMore = personalAgents.length > PERSONAL_LIMIT || pagination?.hasMore || pagination?.total > PERSONAL_LIMIT;

  return (
    <View style={styles.section} testID="sidebar-bots-section">
      <HeaderContainer
        ref={headerRef}
        style={styles.header}
        onHoverIn={Platform.OS !== 'web' ? () => setHeaderHovered(true) : undefined}
        onHoverOut={Platform.OS !== 'web' ? () => setHeaderHovered(false) : undefined}
        onFocus={() => setHeaderFocused(true)}
        onBlur={() => setHeaderFocused(false)}
        testID="sidebar-bots-header"
      >
        <Pressable onPress={toggleBots} accessibilityRole="button" accessibilityLabel={label(t, 'pages.agents.myAgents', 'Your bots')} accessibilityState={{ expanded: !isCollapsed }} aria-expanded={!isCollapsed} style={styles.title}>
          <Text size="s" weight="semibold" color="secondary">
            {label(t, 'pages.agents.myAgents', 'Your bots')}
          </Text>
        </Pressable>
        {canShowPersonal && !isCollapsed && (personalAgents.length + savedAgents.length > 1 || pagination?.hasMore) && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={isReordering ? 'Done reordering bots' : 'Reorder bots'}
            disabled={isLoadingOrder}
            onPress={toggleReordering}
            style={isReordering
              ? [styles.doneButton, { backgroundColor: colors.solid_color_secondary }]
              : [styles.collapse, { opacity: showHeaderControls ? 1 : 0 }]}
          >
            {isReordering
              ? <Text size="s" weight="semibold">Done</Text>
              : <Icon name="gear" size={14} color={colors.text_color_secondary} />}
          </Pressable>
        )}
        <Pressable onPress={toggleBots} accessibilityRole="button" accessibilityLabel={`${isCollapsed ? 'Expand' : 'Collapse'} bots section`} accessibilityState={{ expanded: !isCollapsed }} aria-expanded={!isCollapsed} hitSlop={4} style={[styles.collapse, { opacity: showHeaderControls ? 1 : 0 }]}>
          <Icon name={isCollapsed ? 'chevron-right' : 'chevron-down'} size={14} color={colors.text_color_secondary} />
        </Pressable>
      </HeaderContainer>
      {!isCollapsed && <>
        {isReordering && (
          <View style={styles.message}>
            <Text size="s" color="secondary">{Platform.OS === 'web' && !isXS ? 'Drag bots to reorder. You can also use the up and down arrow keys.' : 'Use the arrows to reorder your bots.'}</Text>
            <Text size="xs" color="tertiary">Changes save automatically on this device.</Text>
          </View>
        )}
        {visiblePersonalAgents.map((agent, index) => renderOrderedAgent(agent, index, personalAgents, 'sidebar_my_bot'))}
        {canShowPersonal && error ? (
          <View style={styles.message}>
            <Text size="s" color="secondary" accessibilityRole="alert">Could not load your bots.</Text>
            <SidebarListItemLayout noEllipsis title={label(t, 'pages.common.retry', 'Retry')} onPress={() => {
              if (!useAgentStore.getState().isLoadingMyAgents) void fetchMyAgents(1);
            }} />
          </View>
        ) : (!isAuthResolved || (canShowPersonal && isLoading && !personalAgents.length)) ? (
          <Text size="s" color="tertiary" style={styles.message} accessibilityLiveRegion="polite">Loading your bots…</Text>
        ) : !personalAgents.length && !savedAgents.length ? (
          <View style={styles.message}>
            <Text size="s" color="tertiary">{canShowPersonal ? 'Hire a bot for work you do often.' : 'Sign in to keep your own bots here.'}</Text>
            <SidebarListItemLayout noEllipsis title={createLabel} icon="plus" onPress={createBot} />
          </View>
        ) : null}
        {canShowPersonal && hasMore && !isReordering && (
          <SidebarListItemLayout noEllipsis title={label(t, 'pages.agents.seeAll', 'See all')} onPress={() => navigate('/agents?category=my-agents')} />
        )}
        {savedAgents.length > 0 && (
          <>
            <Text size="s" color="secondary" style={styles.savedHeading} accessibilityRole="header">Saved bots</Text>
            {savedAgents.map((agent, index) => renderOrderedAgent(agent, index, savedAgents, 'sidebar_saved_agent'))}
          </>
        )}
      </>}
    </View>
  );
}

const styles = StyleSheet.create({
  section: { paddingBottom: 8 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 10, paddingRight: 2, minHeight: 36 },
  title: { flex: 1, minHeight: 36, justifyContent: 'center' },
  reorderGrip: { width: 14, height: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 3 },
  reorderGripColumn: { gap: 3 },
  reorderGripDot: { width: 2.5, height: 2.5, borderRadius: 2 },
  doneButton: { minHeight: 32, paddingHorizontal: 12, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  collapse: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: 6 },
  message: { paddingHorizontal: 10, paddingVertical: 6, gap: 4 },
  savedHeading: { paddingHorizontal: 10, paddingTop: 10, paddingBottom: 6 },
});

export default React.memo(SidebarSavedAgentsSection);
